class EtolieNavigationPanel extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    this.dialog = this.querySelector('[data-etolie-navigation-dialog]');
    this.closeButton = this.querySelector('[data-etolie-navigation-close]');
    this.scrollArea = this.querySelector('[data-etolie-navigation-scroll]');
    this.categorySection = this.querySelector('.etolie-navigation__categories');
    this.firstCategory = this.querySelector('.etolie-navigation__category');
    this.searchTrigger = this.querySelector('[data-etolie-navigation-search]');
    if (!this.dialog) return;

    this._onDocumentClick = this.onDocumentClick.bind(this);
    this._onCancel = this.onCancel.bind(this);
    this._onKeydown = this.onKeydown.bind(this);
    this._onClose = this.onNativeClose.bind(this);
    this._onDialogClick = this.onDialogClick.bind(this);
    this._onSearchClick = this.onSearchClick.bind(this);

    document.addEventListener('click', this._onDocumentClick);
    this.dialog.addEventListener('cancel', this._onCancel);
    this.dialog.addEventListener('keydown', this._onKeydown);
    this.dialog.addEventListener('close', this._onClose);
    this.dialog.addEventListener('click', this._onDialogClick);
    this.searchTrigger?.addEventListener('click', this._onSearchClick);
    window.etolieNavigation = this;
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocumentClick);
    this.dialog?.removeEventListener('cancel', this._onCancel);
    this.dialog?.removeEventListener('keydown', this._onKeydown);
    this.dialog?.removeEventListener('close', this._onClose);
    this.dialog?.removeEventListener('click', this._onDialogClick);
    this.searchTrigger?.removeEventListener('click', this._onSearchClick);
    window.clearTimeout(this._closeTimer);
    window.cancelAnimationFrame(this._closeFrame);
    if (window.etolieNavigation === this) delete window.etolieNavigation;
    this._connected = false;
  }

  onDocumentClick(event) {
    const opener = event.target.closest?.('[data-etolie-navigation-open]');
    if (opener) {
      event.preventDefault();
      this.open(opener, opener.dataset.etolieNavigationFocus || 'start', event.detail === 0);
      return;
    }

    const closer = event.target.closest?.('[data-etolie-navigation-close]');
    if (closer && this.contains(closer)) {
      event.preventDefault();
      this.close(true);
      return;
    }

    // 20.10.2026: link „Cookies” w hamburgerze otwiera okno preferencji Shopify, a ono leży pod
    // otwartym dialogiem (top layer). Zamykamy menu od razu, bez animacji, i puszczamy klik dalej.
    const consent = event.target.closest?.('a[href="#shopifyReshowConsentBanner"]');
    if (consent && this.contains(consent) && this.dialog?.open) {
      this.dialog.classList.remove('is-closing');
      this.dialog.close();
    }
  }

  onCancel(event) {
    event.preventDefault();
    this.close(true);
  }

  onKeydown(event) {
    this.closeButton?.removeAttribute('data-etolie-suppress-focus');
    if (event.key !== 'Escape' || !this.dialog?.open) return;
    event.preventDefault();
    this.close(true);
  }

  onDialogClick(event) {
    if (event.target === this.dialog) this.close(true);
  }

  onSearchClick(event) {
    const searchModal = document.getElementById('search-modal');
    if (!searchModal || typeof searchModal.showDialog !== 'function') return;

    event.preventDefault();
    this.openSearchAfterClose = true;
    this.close(true);
  }

  open(opener, focusTarget = 'start', showInitialFocus = false) {
    if (!this.dialog || this.dialog.open || this.dialog.classList.contains('is-closing')) return;

    this.opener = opener instanceof HTMLElement ? opener : null;
    // `scrollIntoView()` wewnątrz otwartego dialogu oraz zdjęcie blokady scrolla
    // potrafią przestawić dokument pod panelem na początek. Zapamiętujemy oba
    // kierunki przed zmianą stanu i po zamknięciu przywracamy dokładnie ten punkt.
    this.documentScrollPosition = {
      x: window.scrollX,
      y: window.scrollY,
    };
    this.restoreFocus = true;
    this.updateExpandedState(true);
    document.documentElement.classList.add('etolie-navigation-open');

    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');

    window.requestAnimationFrame(() => {
      if (focusTarget === 'categories' && this.categorySection && this.firstCategory) {
        this.categorySection.scrollIntoView({ block: 'start' });
        this.firstCategory.focus({ preventScroll: true });
        return;
      }

      if (this.scrollArea) this.scrollArea.scrollTop = 0;
      this.closeButton?.toggleAttribute('data-etolie-suppress-focus', !showInitialFocus);
      this.closeButton?.focus({ preventScroll: true });
    });
  }

  close(restoreFocus = true) {
    if (!this.dialog?.open || this.dialog.classList.contains('is-closing')) return;
    this.restoreFocus = restoreFocus;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      this.dialog.close();
      return;
    }

    this.dialog.classList.add('is-closing');
    const finishClose = () => {
      if (this.dialog.open && this.dialog.classList.contains('is-closing')) this.dialog.close();
    };

    window.clearTimeout(this._closeTimer);
    window.cancelAnimationFrame(this._closeFrame);
    this._closeTimer = window.setTimeout(finishClose, 1200);
    this._closeFrame = window.requestAnimationFrame(() => {
      let animations = [];
      if (typeof this.dialog.getAnimations === 'function') {
        try {
          animations = this.dialog.getAnimations({ subtree: true });
        } catch (_error) {
          // Starsze wersje Safari nie obsługują opcji `subtree`.
          animations = this.dialog.getAnimations();
        }
        animations = animations.filter((animation) => animation.playState !== 'finished');
      }

      if (!animations.length) {
        finishClose();
        return;
      }

      Promise.allSettled(animations.map((animation) => animation.finished)).then(finishClose);
    });
  }

  onNativeClose() {
    window.clearTimeout(this._closeTimer);
    window.cancelAnimationFrame(this._closeFrame);
    this.dialog.classList.remove('is-closing');
    this.closeButton?.removeAttribute('data-etolie-suppress-focus');
    document.documentElement.classList.remove('etolie-navigation-open');
    this.updateExpandedState(false);

    const opener = this.opener;
    const scrollPosition = this.documentScrollPosition;
    this.opener = null;
    if (this.restoreFocus && opener?.isConnected) opener.focus({ preventScroll: true });
    if (scrollPosition) {
      window.scrollTo(scrollPosition.x, scrollPosition.y);
      // Safari odtwarza pozycję blokowanego dokumentu dopiero w następnej klatce.
      // Drugi, identyczny zapis nie animuje strony i zamyka ten wyścig.
      window.requestAnimationFrame(() => {
        window.scrollTo(scrollPosition.x, scrollPosition.y);
      });
    }
    this.documentScrollPosition = null;
    this.restoreFocus = true;

    if (this.openSearchAfterClose) {
      this.openSearchAfterClose = false;
      window.requestAnimationFrame(() => {
        const searchModal = document.getElementById('search-modal');
        if (searchModal && typeof searchModal.showDialog === 'function') searchModal.showDialog();
      });
    }
  }

  updateExpandedState(expanded) {
    document.querySelectorAll('[data-etolie-navigation-open]').forEach((trigger) => {
      if (trigger.getAttribute('aria-controls') === this.dialog.id) {
        trigger.setAttribute('aria-expanded', String(expanded));
      }
    });
  }
}

if (!customElements.get('etolie-navigation-panel')) {
  customElements.define('etolie-navigation-panel', EtolieNavigationPanel);
}
